import type { KeyBindings, ToolsConfig } from '../types'
import { TOOLBAR_CSS } from './styles'

export type ToolbarMode = 'idle' | 'annotating' | 'frozen'

interface ToolbarCallbacks {
  onToggleAnnotate: (active: boolean) => void
  onFreezeAnimations: (frozen: boolean) => void
  onScreenshot: () => void
  onCopy: () => boolean | Promise<boolean>
  onToggleIssues?: (active: boolean) => void
  onClearPage?: () => void
  onClearAll?: () => void
  onPopOut?: () => void
}

// ── Inline SVG icons (24x24, 2px stroke) ─────────────────────

const ICONS = {
  annotate: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>`,
  freeze: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>`,
  copy: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`,
  issues: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><path d="M3 6h.01"/><path d="M3 12h.01"/><path d="M3 18h.01"/></svg>`,
  check: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`,
  clear: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`,
  screenshot: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>`,
  popOut: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>`,
} as const

export class Toolbar {
  private static readonly VIEWPORT_MARGIN = 16
  private static readonly TOP_MARGIN = 24
  private host!: HTMLElement
  private shadow!: ShadowRoot
  private toolbarEl!: HTMLDivElement
  private annotateBtn!: HTMLButtonElement
  private freezeBtn!: HTMLButtonElement
  private copyBtn!: HTMLButtonElement
  private issuesBtn!: HTMLButtonElement
  private copyResetTimer: ReturnType<typeof setTimeout> | null = null
  private annotateActive = false
  private freezeActive = false
  private issuesActive = false
  private dragging = false
  private dragOffset = { x: 0, y: 0 }
  private readonly boundResize = (): void => {
    this.clampIntoViewport()
  }

  private readonly boundViewportChange = (): void => {
    this.clampIntoViewport()
  }

  private boundDragStart = (evt: Event): void => {
    const e = evt as MouseEvent
    const target = e.target as Element
    if (target.closest('.btn')) return
    this.dragging = true
    const rect = this.host.getBoundingClientRect()
    this.dragOffset = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    e.preventDefault()
  }

  private boundDragMove = (e: MouseEvent): void => {
    if (!this.dragging) return
    const viewport = this.viewportBounds()
    const nextLeft = viewport.left + this.clampAxis(e.clientX - this.dragOffset.x - viewport.left, this.host.offsetWidth, viewport.width)
    const nextTop = viewport.top + this.clampTop(e.clientY - this.dragOffset.y - viewport.top, this.host.offsetHeight, viewport.height)
    Object.assign(this.host.style, {
      left: `${nextLeft}px`,
      bottom: `${window.innerHeight - nextTop - this.host.offsetHeight}px`,
      top: 'auto',
      right: 'auto',
    })
  }

  private boundDragEnd = (): void => {
    if (this.dragging) this.savePosition()
    this.dragging = false
  }

  private keys: KeyBindings

  private readonly tools: ToolsConfig

  private shortcutLabel(value: string | undefined, fallback: string): string {
    return (value ?? fallback).toUpperCase()
  }

  constructor(
    private readonly position: string,
    private readonly callbacks: ToolbarCallbacks,
    keys?: KeyBindings,
    tools?: ToolsConfig,
  ) {
    this.keys = keys ?? {}
    this.tools = tools ?? {}
    this.build()
    this.setupDrag()
  }

  /** Whether a built-in tool should be shown (default true if not specified). */
  private show(id: keyof ToolsConfig): boolean {
    const v = this.tools[id]
    return v !== false
  }

  private build(): void {
    this.host = document.createElement('div')
    this.host.setAttribute('data-visual-qa', 'toolbar')
    this.shadow = this.host.attachShadow({ mode: 'open' })

    const sheet = new CSSStyleSheet()
    sheet.replaceSync(TOOLBAR_CSS)
    this.shadow.adoptedStyleSheets = [sheet]

    // Full toolbar
    this.toolbarEl = document.createElement('div')
    this.toolbarEl.className = 'toolbar'

    // Visible drag handle at the top of the toolbar
    const dragHandle = document.createElement('div')
    dragHandle.className = 'drag-handle'
    dragHandle.setAttribute('aria-label', 'Drag to reposition toolbar')
    dragHandle.innerHTML = `<svg width="16" height="6" viewBox="0 0 16 6" fill="currentColor">
      <circle cx="4" cy="1.5" r="1.2"/><circle cx="8" cy="1.5" r="1.2"/><circle cx="12" cy="1.5" r="1.2"/>
      <circle cx="4" cy="4.5" r="1.2"/><circle cx="8" cy="4.5" r="1.2"/><circle cx="12" cy="4.5" r="1.2"/>
    </svg>`
    this.toolbarEl.appendChild(dragHandle)

    const k = this.keys
    this.annotateBtn = this.makeBtn(ICONS.annotate, `Annotate elements (${this.shortcutLabel(k.annotate, 'a')})`, () => {
      const next = !this.annotateActive
      this.setAnnotateActive(next)
      this.callbacks.onToggleAnnotate(next)
    })

    this.freezeBtn = this.makeBtn(ICONS.freeze, `Freeze page (${this.shortcutLabel(k.freeze, 'f')})`, () => {
      const next = !this.freezeActive
      this.setFreezeActive(next)
      this.callbacks.onFreezeAnimations(next)
    })

    const screenshotBtn = this.makeBtn(ICONS.screenshot, `Screenshot region (${this.shortcutLabel(k.screenshot, 'c')})`, () => {
      this.callbacks.onScreenshot()
    })

    this.copyBtn = this.makeBtn(ICONS.copy, `Copy backend item context (${this.shortcutLabel(k.copy, 'y')})`, async () => {
      const copied = await this.callbacks.onCopy()
      this.showCopyFeedback(copied)
    })

    this.issuesBtn = this.makeBtn(ICONS.issues, `Review backend items (${this.shortcutLabel(k.items, 'i')})`, () => {
      const next = !this.issuesActive
      this.setIssuesActive(next)
      this.callbacks.onToggleIssues?.(next)
    })

    const popOutBtn = this.makeBtn(ICONS.popOut, `Pop out items to side window (${this.shortcutLabel(k.popOut, 'p')})`, () => {
      this.callbacks.onPopOut?.()
    })

    const mkDiv = () => { const d = document.createElement('div'); d.className = 'divider'; return d }
    const toAppend: (HTMLButtonElement | HTMLDivElement)[] = []
    const add = (el: HTMLButtonElement | HTMLDivElement) => {
      if (toAppend.length > 0) toAppend.push(mkDiv())
      toAppend.push(el)
    }
    if (this.show('annotate')) add(this.annotateBtn)
    if (this.show('items')) add(this.issuesBtn)
    if (this.show('popOut') && 'documentPictureInPicture' in window) toAppend.push(popOutBtn)
    if (this.show('screenshot')) add(screenshotBtn)
    if (this.show('freeze')) add(this.freezeBtn)
    if (this.show('copy')) add(this.copyBtn)
    this.toolbarEl.append(...toAppend)
    this.shadow.appendChild(this.toolbarEl)

    // Prevent toolbar clicks from reaching page handlers (e.g. Alpine @click.outside)
    // Shadow DOM stopPropagation only works within the shadow tree — clicks still
    // re-dispatch from the host element into the regular DOM.
    this.host.addEventListener('click', (e) => e.stopPropagation())
    this.host.addEventListener('mousedown', (e) => e.stopPropagation())
    this.host.addEventListener('pointerdown', (e) => e.stopPropagation())

    const root = document.getElementById('visual-qa-root') ?? document.body
    root.appendChild(this.host)
    this.applyPosition()
    if (!this.loadSavedPosition()) this.centerInViewport()
  }

  private showCopyFeedback(copied: boolean): void {
    if (this.copyResetTimer !== null) clearTimeout(this.copyResetTimer)
    this.copyBtn.innerHTML = copied ? ICONS.check : ICONS.copy
    this.copyBtn.setAttribute('data-tooltip', copied ? 'Copied' : 'Copy failed')
    this.copyBtn.setAttribute('aria-label', copied ? 'Copied' : 'Copy failed')
    this.copyResetTimer = setTimeout(() => {
      this.copyBtn.innerHTML = ICONS.copy
      this.copyBtn.setAttribute('data-tooltip', `Copy backend item context (${this.shortcutLabel(this.keys.copy, 'y')})`)
      this.copyBtn.setAttribute('aria-label', `Copy backend item context (${this.shortcutLabel(this.keys.copy, 'y')})`)
      this.copyResetTimer = null
    }, 1200)
  }

  private makeBtn(iconHtml: string, tooltip: string, onClick: () => void | Promise<void>): HTMLButtonElement {
    const btn = document.createElement('button')
    btn.className = 'btn'
    btn.setAttribute('data-tooltip', tooltip)
    btn.setAttribute('aria-label', tooltip)
    btn.innerHTML = iconHtml
    btn.addEventListener('click', (e) => {
      e.stopPropagation()
      void onClick()
    })
    return btn
  }

  private applyPosition(): void {
    const m = '16px'
    Object.assign(this.host.style, {
      position: 'fixed',
      zIndex: '2147483646',
      bottom: this.position.includes('bottom') ? m : 'auto',
      top: this.position.includes('top') ? `${Toolbar.TOP_MARGIN}px` : 'auto',
      right: this.position.includes('right') ? m : 'auto',
      left: this.position.includes('left') ? m : 'auto',
    })
  }

  private static readonly POSITION_KEY = 'visual-qa:toolbar-pos'

  private clamp(value: number, min: number, max: number): number {
    if (max < min) return min
    return Math.min(Math.max(value, min), max)
  }

  private viewportBounds(): { left: number; top: number; width: number; height: number } {
    const viewport = window.visualViewport
    return viewport
      ? { left: viewport.offsetLeft, top: viewport.offsetTop, width: viewport.width, height: viewport.height }
      : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight }
  }

  private clampAxis(offset: number, size: number, viewportSize: number): number {
    const max = Math.max(0, viewportSize - size - Toolbar.VIEWPORT_MARGIN)
    const min = Math.min(Toolbar.VIEWPORT_MARGIN, max)
    return this.clamp(offset, min, max)
  }

  private clampTop(offset: number, size: number, viewportSize: number): number {
    const max = Math.max(0, viewportSize - size - Toolbar.VIEWPORT_MARGIN)
    const min = Math.min(Toolbar.TOP_MARGIN, max)
    return this.clamp(offset, min, max)
  }

  private clampIntoViewport(): void {
    const viewport = this.viewportBounds()
    const rect = this.host.getBoundingClientRect()
    const nextLeft = viewport.left + this.clampAxis(rect.left - viewport.left, rect.width, viewport.width)
    const nextTop = viewport.top + this.clampTop(rect.top - viewport.top, rect.height, viewport.height)
    Object.assign(this.host.style, {
      left: `${nextLeft}px`,
      bottom: `${window.innerHeight - nextTop - rect.height}px`,
      top: 'auto',
      right: 'auto',
    })
  }

  private savePosition(): void {
    const { left, right, top, bottom } = this.host.style
    try {
      localStorage.setItem(Toolbar.POSITION_KEY, JSON.stringify({ left, right, top, bottom }))
    } catch {}
  }

  private loadSavedPosition(): boolean {
    try {
      const raw = localStorage.getItem(Toolbar.POSITION_KEY)
      if (!raw) return false
      const { left, right, top, bottom } = JSON.parse(raw)
      Object.assign(this.host.style, { left, right, top, bottom })
    } catch {
      return false
    }
    this.clampIntoViewport()
    return true
  }

  private centerInViewport(): void {
    const viewport = this.viewportBounds()
    const left = viewport.left + Math.max(0, (viewport.width - this.host.offsetWidth) / 2)
    const top = viewport.top + Math.max(Toolbar.TOP_MARGIN, (viewport.height - this.host.offsetHeight) / 2)
    Object.assign(this.host.style, {
      left: `${left}px`,
      top: `${top}px`,
      right: 'auto',
      bottom: 'auto',
    })
    this.clampIntoViewport()
  }

  private setupDrag(): void {
    this.shadow.addEventListener('mousedown', this.boundDragStart)
    document.addEventListener('mousemove', this.boundDragMove)
    document.addEventListener('mouseup', this.boundDragEnd)
    window.addEventListener('resize', this.boundResize)
    window.visualViewport?.addEventListener('resize', this.boundViewportChange)
    window.visualViewport?.addEventListener('scroll', this.boundViewportChange)
  }

  setVisible(visible: boolean): void {
    this.host.style.display = visible ? '' : 'none'
  }

  setAnnotateActive(active: boolean): void {
    this.annotateActive = active
    this.annotateBtn.classList.toggle('active', active)
    document.body.classList.toggle('ik-annotating', active)
  }

  setFreezeActive(active: boolean): void {
    this.freezeActive = active
    this.freezeBtn.classList.toggle('active', active)
  }

  setIssuesActive(active: boolean): void {
    this.issuesActive = active
    this.issuesBtn.classList.toggle('active', active)
  }

  // Keep for compatibility — resolves visual mode from the core controller.
  setMode(mode: ToolbarMode): void {
    this.setAnnotateActive(mode === 'annotating')
  }

  setAnnotationCount(count: number): void {
    let badge = this.annotateBtn.querySelector('.badge')
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span')
        badge.className = 'badge'
        this.annotateBtn.appendChild(badge)
      }
      badge.textContent = count > 99 ? '99+' : String(count)
    } else {
      badge?.remove()
    }
  }

  setTotalCount(_count: number): void {
  }

  destroy(): void {
    if (this.copyResetTimer !== null) clearTimeout(this.copyResetTimer)
    this.shadow.removeEventListener('mousedown', this.boundDragStart)
    document.removeEventListener('mousemove', this.boundDragMove)
    document.removeEventListener('mouseup', this.boundDragEnd)
    window.removeEventListener('resize', this.boundResize)
    window.visualViewport?.removeEventListener('resize', this.boundViewportChange)
    window.visualViewport?.removeEventListener('scroll', this.boundViewportChange)
    this.host.remove()
    document.body.classList.remove('ik-annotating')
  }
}
